// src/pages/LeadGeneration.js — Lead Generation (2026-10-19)
//
// Owner-only prospecting: an AI scout searches the web for organizations that
// look ready to buy managed IT / web development (or to partner with MIDTC),
// our own website + email-DNS check adds hard facts, and every lead gets a
// 0-100 score with the arithmetic shown. Access is enforced SERVER-side
// (backend/src/leadGen.js): anyone not on the allow-list gets a 404 from every
// /api/leadgen route, and this page then shows "not available".
//
// 2026-10-20 (Juwan: leads must be properly vetted, with real contact details,
// a full-proposal view for bids and an intro email): every lead shows who to
// contact (read from its own website, each with the page it came from and a
// confirmed / not-confirmed badge), a vetting checklist, a Bids view with the
// full RFP, and a welcoming intro email draft that staff copy or open in their
// own mail app. Nothing here sends email.
//
// 2026-10-22 (Juwan): Assign gives a lead to a staff member. On its follow-up
// date the server emails them a reminder and puts it on their My Day list.
// ?lead=<id> opens that lead (the link in the reminder email and on My Day).
// 2026-10-23: a follow-up date saved on an unassigned lead assigns it to the
// person who saved it (server side), so the 8 AM reminder has someone to go to.
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";

const API = (process.env.REACT_APP_API_URL || "https://ticketing.midtc.com") + "/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const call = async (path, opts = {}) => {
  const r = await fetch(API + path, {
    ...opts,
    headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  const body = r.headers.get("content-type")?.includes("json") ? await r.json().catch(() => ({})) : null;
  if (!r.ok) { const e = new Error(body?.error || `HTTP ${r.status}`); e.status = r.status; throw e; }
  return body;
};

const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };
const btn = (bg, extra = {}) => ({ background: bg, color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", ...extra });
const ghost = { background: "transparent", color: "var(--t-text-body)", border: "1px solid var(--t-border-strong)", borderRadius: 8, padding: "7px 13px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const input = { background: "var(--t-surface-mid)", border: "1px solid var(--t-border-strong)", borderRadius: 8, padding: "8px 11px", color: "var(--t-text-primary)", fontSize: 13, width: "100%", boxSizing: "border-box" };
const label = { fontSize: 11, color: "var(--t-text-faint)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, display: "block" };
const small = { fontSize: 12, color: "var(--t-text-faint)" };
const ACCENT = "var(--t-accent, #4f6fff)";
const GOOD = "#34d399", WARN = "#fbbf24", BAD = "#f87171", MUTED = "#94a3b8";

const GRADE = {
  A: { c: "#34d399", bg: "rgba(52,211,153,0.14)", word: "Hot" },
  B: { c: "#fbbf24", bg: "rgba(251,191,36,0.14)", word: "Warm" },
  C: { c: "#fb923c", bg: "rgba(251,146,60,0.13)", word: "Cool" },
  D: { c: "#94a3b8", bg: "rgba(148,163,184,0.13)", word: "Cold" },
};
const STATUS_LABEL = { new: "New", reviewing: "Reviewing", contacted: "Contacted", qualified: "Qualified", proposal: "Proposal sent", won: "Won", lost: "Lost", not_a_fit: "Not a fit" };
const SERVICE_LABEL = { managed_it: "Managed IT", m365_security: "Email & security", website_build: "Website build", website_maintenance: "Website care", digital_publishing: "Digital publishing", other: "Other" };
const SIGNAL_LABEL = { rfp: "Open RFP / bid", provider_change: "Changing provider", stated_pain: "Stated IT pain", job_posting: "Hiring IT / web role", security_incident: "Security incident", growth_event: "Growth event", partner_fit: "Partner fit", no_website: "No website", social_only: "Facebook / social only", outdated_tech: "Old or basic website", other: "Other" };
const PRESENCE_LABEL = { listing_only: "No website — Google / directory listing only", social_only: "No website — social media page only", outdated_site: "Website looks outdated", basic_site: "Very basic website", modern_site: "Modern website" };
const WEAK = ["listing_only", "social_only", "outdated_site", "basic_site"];
const ROLE_LABEL = { decision_maker: "Decision-maker", procurement: "Bid contact", board: "Board", staff: "Staff", department: "Department", general: "Main inbox" };
const VET = {
  vetted: { c: GOOD, word: "Vetted" },
  partial: { c: WARN, word: "Partly vetted" },
  unverified: { c: MUTED, word: "Not vetted" },
};
const SUBMIT_LABEL = { portal: "Online portal", email: "Email", mail_or_hand_delivery: "Mail or hand delivery", mixed: "More than one way", unknown: "Not stated" };

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const moneyShort = (n) => { n = Number(n) || 0; return n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `$${Math.round(n / 1000)}k` : money(n); };
const et = (ts, withTime = true) => !ts ? "" : new Date(ts).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", ...(withTime ? { hour: "numeric", minute: "2-digit" } : { year: "numeric" }) });
const todayEt = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const day = (ymd) => !ymd ? "" : new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric", year: "numeric" });
const dueWords = (n) => n == null ? "No due date found" : n < 0 ? `Closed ${-n} day${n === -1 ? "" : "s"} ago` : n === 0 ? "Due today" : `${n} day${n === 1 ? "" : "s"} left`;
const dueColor = (n) => n == null ? MUTED : n < 0 ? MUTED : n <= 7 ? BAD : n <= 21 ? WARN : GOOD;
const shortUrl = (u) => { try { const x = new URL(u); return (x.hostname.replace(/^www\./, "") + x.pathname).replace(/\/$/, ""); } catch { return u; } };
const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
// "citydesk@…" → "City desk" when a department inbox has no label on the page.
const INBOX_WORDS = { info: "General inbox", contact: "General inbox", hello: "General inbox", office: "Office", admin: "Office", sales: "Sales", adsales: "Ad sales", support: "Support", billing: "Billing", accounting: "Accounting", hr: "Human resources", jobs: "Hiring", citydesk: "City desk", news: "Newsroom", editor: "Editor", circ: "Circulation", class: "Classifieds" };
const inboxLabel = (email) => { const l = String(email || "").split("@")[0].toLowerCase(); return INBOX_WORDS[l] || l.replace(/[._-]+/g, " ").replace(/^\w/, c => c.toUpperCase()); };
const contactKey = (c) => (c.email ? `e:${c.email.toLowerCase()}` : `p:${String(c.phone || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")}|${String(c.name || "").toLowerCase()}`);
const copy = (text) => navigator.clipboard?.writeText(text || "");
// "James Harrick" → "James H." (two Jameses on staff).
const shortName = (n) => { const p = String(n || "").split(" ").filter(Boolean); return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : p[0] || ""; };

function Stat({ label: l, value, sub, color }) {
  return (
    <div style={{ ...panel, padding: "13px 15px", minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "var(--t-text-faint)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || "var(--t-text-primary)", lineHeight: 1.25, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{sub}</div>}
    </div>
  );
}

function GradeBadge({ grade, score, big }) {
  const g = GRADE[grade] || GRADE.D;
  return (
    <span title={`${g.word} lead`} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: g.bg, color: g.c, borderRadius: 8, padding: big ? "6px 12px" : "3px 8px", fontWeight: 800, fontSize: big ? 18 : 13, whiteSpace: "nowrap" }}>
      {score ?? 0}<span style={{ fontSize: big ? 12 : 10, fontWeight: 700, opacity: 0.85 }}>{grade || "D"} · {g.word}</span>
    </span>
  );
}

function Chip({ children, color, title }) {
  return <span title={title} style={{ display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 6, marginRight: 4, marginBottom: 3, color: color || "var(--t-text-body)", border: "1px solid var(--t-border-soft)", whiteSpace: "nowrap" }}>{children}</span>;
}

function VetChip({ v }) {
  const s = VET[v?.status] || { c: MUTED, word: "Not vetted yet" };
  return <Chip color={s.c} title={v?.checked_at ? `Checked ${et(v.checked_at)} ET` : "Vetting runs automatically; or use Re-check & vet"}>{v?.status === "vetted" ? "✓ " : ""}{s.word}</Chip>;
}

// "Local Opportunity" — within the local radius of the home base (measured server-side).
function LocalTag({ miles }) {
  return (
    <span title={miles != null ? `${miles} miles from the office` : "Near the office"} style={{ display: "inline-block", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 6, marginBottom: 4, color: "#2dd4bf", background: "rgba(45,212,191,0.14)", border: "1px solid rgba(45,212,191,0.35)", whiteSpace: "nowrap" }}>
      Local Opportunity{miles != null ? ` · ${miles} mi` : ""}
    </span>
  );
}

function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ ...panel, background: "var(--t-page, #111b2f)", width: "100%", maxWidth: width, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t-text-primary)", minWidth: 0 }}>{title}</div>
          <button onClick={onClose} style={{ ...ghost, padding: "3px 10px" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Score breakdown ──────────────────────────────────────────
function Breakdown({ b }) {
  if (!b) return null;
  const rows = [["fit", "Fit"], ["intent", "Buying intent"], ["opportunity", "Opportunity"], ["reachability", "Reachability"]];
  return rows.map(([k, name]) => {
    const part = b[k] || { pts: 0, max: 0, why: [] };
    const pct = part.max ? Math.round((part.pts / part.max) * 100) : 0;
    return (
      <div key={k} style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--t-text-body)", marginBottom: 3 }}>
          <b>{name}</b><span style={{ fontWeight: 700 }}>{part.pts} / {part.max}</span>
        </div>
        <div style={{ height: 6, background: "var(--t-border-soft)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: pct >= 70 ? "#34d399" : pct >= 40 ? "#fbbf24" : "#fb923c" }} />
        </div>
        <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--t-text-faint)" }}>
          {(part.why || []).map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      </div>
    );
  });
}

function SiteFacts({ sc, lead }) {
  if (!sc) return (
    <div style={{ fontSize: 12, color: "var(--t-text-faint)" }}>
      {lead?.web_presence && PRESENCE_LABEL[lead.web_presence]
        ? <span style={{ color: "#fb923c", fontWeight: 700 }}>{PRESENCE_LABEL[lead.web_presence]}.</span>
        : "No website on file, so nothing was checked."}
      {lead?.social_url && <> <a href={lead.social_url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>Open their page</a></>}
    </div>
  );
  const row = (k, v, bad) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "3px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
      <span style={{ color: "var(--t-text-faint)" }}>{k}</span>
      <span style={{ color: bad ? "#fb923c" : "var(--t-text-body)", fontWeight: 600, textAlign: "right" }}>{v}</span>
    </div>
  );
  const yr = new Date().getFullYear();
  return (
    <div>
      {row("Website loads", sc.reachable ? `Yes (${sc.status})` : `No${sc.error ? ` — ${sc.error}` : ""}`, !sc.reachable)}
      {sc.reachable && row("HTTPS", sc.https ? "Yes" : "No", !sc.https)}
      {sc.reachable && row("Mobile-ready", sc.mobile_viewport ? "Yes" : "No", !sc.mobile_viewport)}
      {sc.reachable && row("Built with", sc.cms ? `${sc.cms}${sc.cms_version ? ` ${sc.cms_version}` : ""}` : "Unknown")}
      {sc.reachable && sc.copyright_year && row("Footer year", sc.copyright_year, sc.copyright_year <= yr - 2)}
      {sc.reachable && row("Load time", sc.load_ms ? `${(sc.load_ms / 1000).toFixed(1)}s` : "—", sc.load_ms > 4000)}
      {sc.reachable && sc.words != null && !sc.spa && row("Homepage text", `${sc.words.toLocaleString()} words`, sc.words < 120)}
      {sc.reachable && sc.old_markup && row("Code", "Old-style (looks dated)", true)}
      {sc.email && row("Email", sc.email.provider)}
      {sc.email && sc.email.provider !== "None" && row("SPF", sc.email.spf ? "Yes" : "Missing", !sc.email.spf)}
      {sc.email && sc.email.provider !== "None" && row("DMARC", sc.email.dmarc_policy ? `p=${sc.email.dmarc_policy}` : "Missing", !sc.email.dmarc_policy || sc.email.dmarc_policy === "none")}
      <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 5 }}>Checked {et(sc.checked_at)} ET</div>
    </div>
  );
}

// ── Vetting checklist ────────────────────────────────────────
function Vetting({ v }) {
  if (!v) return <div style={small}>Not vetted yet. Click <b>Re-check & vet</b> (free) or <b>Vet with AI</b>.</div>;
  return (
    <div>
      {(v.checks || []).map((c, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: "var(--t-text-body)", padding: "4px 0" }}>
          <span style={{ width: 16, flexShrink: 0, fontWeight: 800, color: c.ok === true ? GOOD : c.ok === false ? BAD : WARN }}>{c.ok === true ? "✓" : c.ok === false ? "✕" : "•"}</span>
          <span style={{ minWidth: 0 }}>
            {c.label}
            {c.url && <> · <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, fontSize: 12, wordBreak: "break-all" }}>{shortUrl(c.url)}</a></>}
          </span>
        </div>
      ))}
      <div style={{ ...small, fontSize: 11, marginTop: 4 }}>
        Checked {et(v.checked_at)} ET{(v.pages || []).length ? ` · read ${v.pages.length} page${v.pages.length === 1 ? "" : "s"} of their website` : ""}
      </div>
    </div>
  );
}

// ── Who to contact ───────────────────────────────────────────
function ContactList({ lead, onUse, onRemove }) {
  const [all, setAll] = useState(false);
  const cs = lead.contacts || [];
  if (!cs.length) {
    return <div style={small}>No contacts found yet.{lead.domain ? " Their website doesn't list any, or blocks automatic reading. Try Vet with AI." : " There's no website to read. Try Vet with AI."}</div>;
  }
  const shown = all ? cs : cs.slice(0, 8);
  const primary = (lead.contact_email || "").toLowerCase();
  return (
    <div>
      {shown.map(c => {
        const isPrimary = c.email && c.email === primary;
        return (
          <div key={contactKey(c)} style={{ padding: "8px 0", borderBottom: "1px solid var(--t-border-soft)", fontSize: 13, color: "var(--t-text-body)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline", justifyContent: "space-between" }}>
              <div style={{ minWidth: 0 }}>
                <b style={{ color: "var(--t-text-primary)" }}>{c.name || c.title || inboxLabel(c.email) || "Main line"}</b>
                {c.name && c.title && <span style={{ color: "var(--t-text-faint)" }}> · {c.title}</span>}
                {" "}<Chip color={c.role === "decision_maker" || c.role === "procurement" ? GOOD : undefined}>{ROLE_LABEL[c.role] || "Contact"}</Chip>
                {isPrimary && <Chip color={ACCENT}>Primary</Chip>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {!isPrimary && c.email && <button onClick={() => onUse(c)} style={{ ...ghost, padding: "1px 8px", fontSize: 11 }}>Make primary</button>}
                <button onClick={() => onRemove(c)} title="Remove this contact" style={{ ...ghost, padding: "1px 7px", fontSize: 11 }}>✕</button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", marginTop: 2 }}>
              {c.email && <span><a href={`mailto:${c.email}`} style={{ color: ACCENT }}>{c.email}</a> <button onClick={() => copy(c.email)} title="Copy the email address" style={{ ...ghost, padding: "0 6px", fontSize: 10, marginLeft: 2 }}>Copy</button></span>}
              {c.phone && <a href={`tel:${c.phone.replace(/ ext\. .*/, "")}`} style={{ color: "var(--t-text-body)" }}>{c.phone}</a>}
              {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>LinkedIn</a>}
            </div>
            <div style={{ fontSize: 11, marginTop: 2, color: c.verified ? GOOD : WARN }}>
              {c.verified
                ? <>✓ Confirmed on <a href={c.verified_on} target="_blank" rel="noopener noreferrer" style={{ color: GOOD, wordBreak: "break-all" }}>{shortUrl(c.verified_on)}</a></>
                : <>Not confirmed on any page{c.name_confirmed ? " (the name is, the email isn't)" : ""}{c.found_by === "ai" ? " — reported by the AI" : c.found_by === "manual" ? " — entered by hand" : ""}{c.source_url ? <> · <a href={c.source_url} target="_blank" rel="noopener noreferrer" style={{ color: WARN }}>source</a></> : ""}</>}
            </div>
          </div>
        );
      })}
      {cs.length > 8 && <button onClick={() => setAll(v => !v)} style={{ ...ghost, padding: "3px 10px", fontSize: 12, marginTop: 8 }}>{all ? "Show fewer" : `Show all ${cs.length}`}</button>}
    </div>
  );
}

function CompanyInfo({ lead }) {
  const i = lead.company_info || {};
  const socials = Object.entries(i.socials || {});
  if (!i.address && !i.main_phone && !i.general_email && !i.hours && !i.ownership && !socials.length) return null;
  const row = (k, v) => v ? <div style={{ display: "flex", gap: 10, fontSize: 13, padding: "3px 0" }}><span style={{ ...small, width: 92, flexShrink: 0 }}>{k}</span><span style={{ color: "var(--t-text-body)", minWidth: 0 }}>{v}</span></div> : null;
  return (
    <div>
      {row("Address", i.address)}
      {row("Main phone", i.main_phone)}
      {row("Email", i.general_email)}
      {row("Hours", i.hours)}
      {row("Ownership", i.ownership)}
      {socials.length > 0 && row("Online", <span>{socials.map(([k, u]) => <a key={k} href={u} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, marginRight: 10, textTransform: "capitalize" }}>{k === "x" ? "X" : k}</a>)}</span>)}
    </div>
  );
}

// ── Intro email draft ────────────────────────────────────────
function IntroEmail({ lead, onSaved }) {
  const o = lead.outreach;
  const [subject, setSubject] = useState(o?.subject || "");
  const [body, setBody] = useState(o?.body || "");
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { setSubject(o?.subject || ""); setBody(o?.body || ""); }, [o?.subject, o?.body, lead.id]);
  const draft = async () => {
    setWorking(true); setMsg("");
    try { onSaved(await call(`/leadgen/leads/${lead.id}/email-draft`, { method: "POST" })); }
    catch (e) { setMsg(e.message); }
    setWorking(false);
  };
  const save = async () => {
    setMsg("");
    try { onSaved(await call(`/leadgen/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify({ outreach: { subject, body } }) })); setMsg("Saved."); }
    catch (e) { setMsg(e.message); }
  };
  const mailto = o ? `mailto:${encodeURIComponent(o.to_email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}` : "#";
  if (!o) {
    return (
      <div>
        <div style={{ ...small, marginBottom: 8 }}>A welcoming first email to {lead.contact_name || "their best contact"}, written from the vetted facts on this page. Nothing is sent: you copy it or open it in your own email.</div>
        <button onClick={draft} disabled={working} style={btn(ACCENT, { opacity: working ? 0.6 : 1 })}>{working ? "Writing… (about 30 seconds)" : "Draft intro email"}</button>
        {msg && <div style={{ fontSize: 12, color: BAD, marginTop: 8 }}>{msg}</div>}
      </div>
    );
  }
  const changed = subject !== (o.subject || "") || body !== (o.body || "");
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--t-text-body)", marginBottom: 8 }}>
        To: <b>{o.to_name || "—"}</b>{o.to_email ? <> &lt;{o.to_email}&gt;</> : <span style={{ color: WARN }}> (no confirmed email; add one before sending)</span>}
      </div>
      <span style={label}>Subject</span>
      <input style={{ ...input, marginBottom: 8 }} value={subject} onChange={e => setSubject(e.target.value)} />
      <span style={label}>Message</span>
      <textarea style={{ ...input, minHeight: 260, resize: "vertical", lineHeight: 1.5, fontFamily: "inherit" }} value={body} onChange={e => setBody(e.target.value)} />
      {o.note && <div style={{ fontSize: 12, color: WARN, marginTop: 6 }}>Note: {o.note}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        <a href={mailto} style={{ ...btn(ACCENT), textDecoration: "none", display: "inline-block" }}>Open in my email</a>
        <button onClick={() => { copy(`Subject: ${subject}\n\n${body}`); setMsg("Copied."); }} style={ghost}>Copy</button>
        {changed && <button onClick={save} style={ghost}>Save edits</button>}
        <button onClick={draft} disabled={working} style={ghost}>{working ? "Writing…" : "Write a new draft"}</button>
      </div>
      <div style={{ ...small, fontSize: 11, marginTop: 6 }}>
        Drafted {et(o.generated_at)} ET{o.generated_by ? ` for ${o.generated_by}` : ""}{o.edited ? " · edited" : ""}. Read it before sending.{msg ? ` ${msg}` : ""}
      </div>
    </div>
  );
}

// ── Full RFP ─────────────────────────────────────────────────
function DocModal({ lead, doc, onClose }) {
  const [text, setText] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch(`${API}/leadgen/leads/${lead.id}/bid/docs/${doc.n}?as=text`, { headers: { Authorization: `Bearer ${tok()}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); return r.text(); })
      .then(setText).catch(e => setErr(e.message));
  }, [lead.id, doc.n]);
  return (
    <Modal title={doc.name} onClose={onClose} width={900}>
      {err ? <div style={{ fontSize: 13, color: BAD }}>{err}</div>
        : text == null ? <div style={small}>Loading…</div>
          : <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, lineHeight: 1.45, color: "var(--t-text-body)", maxHeight: "70vh", overflowY: "auto", margin: 0 }}>{text.replace(/\f/g, "\n\n──────── next page ────────\n\n") || "This document has no readable text (it may be a scanned image). Open the original."}</pre>}
    </Modal>
  );
}

async function openOriginal(lead, doc) {
  // Open the tab first (inside the click) so pop-up blockers allow it, then load the file into it.
  const w = window.open("", "_blank");
  try {
    const r = await fetch(`${API}/leadgen/leads/${lead.id}/bid/docs/${doc.n}`, { headers: { Authorization: `Bearer ${tok()}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const url = URL.createObjectURL(await r.blob());
    if (w) w.location = url; else window.location.assign(url);
  } catch (e) { if (w) w.close(); alert(`Couldn't open the document: ${e.message}`); }
}

function BidBrief({ lead, busy, onStart }) {
  const [doc, setDoc] = useState(null);
  const b = lead.bid;
  const rfp = (lead.signals || []).find(s => s.type === "rfp");
  if (!b) {
    return (
      <div>
        {rfp && <div style={{ fontSize: 13, color: "var(--t-text-body)", marginBottom: 8 }}>{rfp.detail}</div>}
        <div style={{ ...small, marginBottom: 8 }}>
          {lead.bid_due ? <><b style={{ color: dueColor(lead.bid_days_left) }}>{dueWords(lead.bid_days_left)}</b> · due {day(lead.bid_due)}. </> : null}
          Get full RFP downloads the solicitation documents, keeps their full text here, and writes a brief: scope, dates, how to submit, who to contact and any contact rules.
        </div>
        <button onClick={onStart} disabled={busy} title={busy ? "An AI job is already running" : ""} style={btn(ACCENT, { opacity: busy ? 0.5 : 1 })}>Get full RFP</button>
      </div>
    );
  }
  const row = (k, v) => v ? <div style={{ display: "flex", gap: 10, fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--t-border-soft)" }}><span style={{ ...small, width: 120, flexShrink: 0 }}>{k}</span><span style={{ color: "var(--t-text-body)", minWidth: 0, whiteSpace: "pre-wrap" }}>{v}</span></div> : null;
  const list = (title, items) => (items || []).length ? (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 3 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.5 }}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;
  const c = b.contact || {};
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: "var(--t-text-primary)" }}>{b.title || "Solicitation"}</div>
      <div style={{ ...small, marginBottom: 8 }}>
        {[b.issuer, b.solicitation_number ? `#${b.solicitation_number}` : "", b.status !== "unknown" ? b.status : ""].filter(Boolean).join(" · ")}
        {b.source_url && <> · <a href={b.source_url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>Solicitation page</a></>}
      </div>
      <div style={{ ...panel, padding: "10px 12px", marginBottom: 10, display: "flex", flexWrap: "wrap", gap: "4px 18px", alignItems: "baseline" }}>
        <b style={{ color: dueColor(lead.bid_days_left), fontSize: 15 }}>{dueWords(lead.bid_days_left)}</b>
        <span style={{ fontSize: 13, color: "var(--t-text-body)" }}>Due {b.due_date ? day(b.due_date) : "date not stated"}{b.due_time ? `, ${b.due_time}` : ""}</span>
        {b.questions_due && <span style={{ fontSize: 13, color: "var(--t-text-body)" }}>Questions due: {b.questions_due}</span>}
      </div>
      {b.contact_rules && <div style={{ fontSize: 13, color: WARN, border: `1px solid ${WARN}55`, borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}><b>Contact rules:</b> {b.contact_rules}</div>}
      {b.scope_summary && <div style={{ fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.55, marginBottom: 6 }}>{b.scope_summary}</div>}
      {list("What they want", b.scope_items)}
      {list("The proposal must include", b.requirements)}
      {list("How it's scored", b.evaluation_criteria)}
      <div style={{ marginTop: 10 }}>
        {row("How to submit", [SUBMIT_LABEL[b.submission_method] || "", b.submission_details].filter(Boolean).join(" — "))}
        {row("Pre-bid meeting", b.prebid_meeting)}
        {row("Contract term", b.contract_term)}
        {row("Budget", b.budget)}
        {row("Insurance / bonds", b.insurance_and_bonding)}
        {row("Bid contact", [c.name, c.title, c.email, c.phone].filter(Boolean).join(" · "))}
        {(b.key_dates || []).map((d, i) => <div key={i}>{row(d.label, d.date)}</div>)}
      </div>
      {b.fit_for_midtc && <div style={{ fontSize: 13, color: "var(--t-text-body)", marginTop: 10 }}><b>Fit for MIDTC:</b> {b.fit_for_midtc}</div>}
      {list("Risks", b.risks)}
      {list("Questions to submit", b.questions_to_ask)}
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 4 }}>Full documents</div>
        {(b.documents || []).length === 0 && <div style={small}>No documents could be downloaded (the portal may require a login). Use the solicitation page link above.</div>}
        {(b.documents || []).map(d => (
          <div key={d.n} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
            <span style={{ flex: "1 1 220px", minWidth: 0, color: "var(--t-text-body)" }}>
              {d.name}<span style={small}>{d.error ? ` — couldn't download (${d.error})` : ` · ${d.kind === "docx" ? "Word" : d.kind === "text" ? "Text copy" : "PDF"}${d.pages ? `, ${d.pages} pages` : ""}, ${kb(d.bytes)}${d.via === "ai_reader" ? " · copy read by the AI (the town's server didn't answer ours)" : ""}`}</span>
            </span>
            {!d.error && d.chars > 0 && <button onClick={() => setDoc(d)} style={{ ...ghost, padding: "2px 9px", fontSize: 12 }}>Read full text</button>}
            {!d.error && d.kind !== "text" && <button onClick={() => openOriginal(lead, d)} style={{ ...ghost, padding: "2px 9px", fontSize: 12 }}>Open original</button>}
            <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT }}>Source</a>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
        <button onClick={onStart} disabled={busy} style={{ ...ghost, fontSize: 12, opacity: busy ? 0.5 : 1 }}>Read the RFP again</button>
        <span style={{ ...small, fontSize: 11 }}>Brief written {et(b.built_at)} ET from the documents. Check the originals before you submit.</span>
      </div>
      {doc && <DocModal lead={lead} doc={doc} onClose={() => setDoc(null)} />}
    </div>
  );
}

// ── Lead detail drawer ───────────────────────────────────────
function LeadDrawer({ lead, staff, onClose, onSaved, onDeleted, onRunStarted, busy, isMobile }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  useEffect(() => {
    setF({
      status: lead.status, notes: lead.notes || "", next_follow_up: lead.next_follow_up || "",
      contact_name: lead.contact_name || "", contact_title: lead.contact_title || "", contact_email: lead.contact_email || "",
      contact_phone: lead.contact_phone || "", website: lead.website || "",
      est_monthly_value: lead.est_monthly_value, est_project_value: lead.est_project_value,
    });
  }, [lead]);
  useEffect(() => { setMsg(""); }, [lead.id]);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const save = async (extra = {}) => {
    setSaving(true); setMsg("");
    try {
      const body = {
        status: f.status, notes: f.notes, next_follow_up: f.next_follow_up || null,
        contact_name: f.contact_name, contact_title: f.contact_title, contact_email: f.contact_email, contact_phone: f.contact_phone,
        ...extra,
      };
      if (f.website !== (lead.website || "")) body.website = f.website;
      if (Number(f.est_monthly_value) !== lead.est_monthly_value || Number(f.est_project_value) !== lead.est_project_value) {
        body.est_monthly_value = Number(f.est_monthly_value) || 0;
        body.est_project_value = Number(f.est_project_value) || 0;
      }
      onSaved(await call(`/leadgen/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify(body) }));
      setMsg("Saved.");
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };
  const patch = async (body, done) => {
    setMsg("");
    try { onSaved(await call(`/leadgen/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify(body) })); if (done) setMsg(done); }
    catch (e) { setMsg(e.message); }
  };
  const recheck = async () => {
    setSaving(true); setMsg("Reading their website and re-checking sources…");
    try { onSaved(await call(`/leadgen/leads/${lead.id}/recheck`, { method: "POST" })); setMsg("Website, contacts and sources re-checked."); }
    catch (e) { setMsg(e.message); }
    setSaving(false);
  };
  const startJob = async (path, words) => {
    setMsg("");
    try { const r = await call(`/leadgen/leads/${lead.id}/${path}`, { method: "POST" }); onRunStarted(r.run); setMsg(words); }
    catch (e) { setMsg(e.message); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${lead.company_name}? This can't be undone.`)) return;
    try { await call(`/leadgen/leads/${lead.id}`, { method: "DELETE" }); onDeleted(lead.id); }
    catch (e) { setMsg(e.message); }
  };
  const assignee = staff.find(s => s.id === lead.assigned_to);
  const assign = (id) => {
    setAssignOpen(false);
    const who = staff.find(s => s.id === id);
    const fu = lead.next_follow_up;
    const early = Number(new Date().toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hour12: false })) < 8;
    patch({ assigned_to: id }, !id ? "Unassigned." : ["won", "lost", "not_a_fit"].includes(lead.status) ? `Assigned to ${who?.name}.`
      : `Assigned to ${who?.name}. ${!fu ? "Set a follow-up date below to send them a reminder."
      : fu > todayEt() ? `They'll get a reminder email and a My Day task on ${day(fu)} at 8 AM ET.`
        : `The follow-up is due, so the reminder email and My Day task go out ${early ? "at 8 AM ET" : "now"}.`}`);
  };
  const useContact = (c) => patch({ contact_name: c.name || "", contact_title: c.title || inboxLabel(c.email) || "", contact_email: c.email || "", contact_phone: c.phone || lead.contact_phone || "" }, `${c.name || c.email} is now the primary contact.`);
  const removeContact = (c) => { if (window.confirm(`Remove ${c.name || c.email || c.phone} from this lead?`)) patch({ remove_contact: contactKey(c) }, "Contact removed."); };

  const section = (title, children, extra) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 7 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--t-text-primary)", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
        {extra}
      </div>
      {children}
    </div>
  );
  const needs = lead.insights?.needs || [];
  const points = lead.insights?.talking_points || [];
  const verifiedCount = (lead.contacts || []).filter(c => c.verified).length;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 300 }}>
      <aside onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: isMobile ? "100%" : 640, background: "var(--t-page, #111b2f)", borderLeft: "1px solid var(--t-border-soft)", overflowY: "auto", padding: 20, boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            {lead.is_local && <LocalTag miles={lead.distance_miles} />}
            <div style={{ fontSize: 19, fontWeight: 800, color: "var(--t-text-primary)" }}>{lead.company_name}</div>
            <div style={{ fontSize: 12, color: "var(--t-text-faint)", marginTop: 2 }}>
              {lead.lead_type === "partner" ? "Partner" : "Client"}{lead.industry ? ` · ${lead.industry}` : ""}{lead.location ? ` · ${lead.location}` : ""}{lead.employee_estimate ? ` · ~${lead.employee_estimate} staff` : ""}{!lead.is_local && lead.distance_miles != null ? ` · ${lead.distance_miles.toLocaleString()} mi away` : ""}
            </div>
            {lead.website && <a href={lead.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: ACCENT }}>{lead.domain || lead.website}</a>}
            {WEAK.includes(lead.web_presence) && <div style={{ fontSize: 12, color: "#fb923c", fontWeight: 700, marginTop: 2 }}>{PRESENCE_LABEL[lead.web_presence]}</div>}
            {lead.social_url && <a href={lead.social_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT, display: "block" }}>{/facebook\.com/i.test(lead.social_url) ? "Facebook page" : /instagram\.com/i.test(lead.social_url) ? "Instagram page" : "Business listing"}</a>}
          </div>
          <button onClick={onClose} style={{ ...ghost, padding: "3px 10px" }}>✕</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "10px 0 16px" }}>
          <GradeBadge grade={lead.grade} score={lead.score} big />
          <VetChip v={lead.vetting} />
          {lead.is_bid && <Chip color={dueColor(lead.bid_days_left)}>Bid · {dueWords(lead.bid_days_left)}</Chip>}
          <span style={{ fontSize: 13, color: "var(--t-text-body)" }}>Est. <b>{money(lead.est_annual_value)}</b>/yr</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          <button onClick={() => startJob("research", "Vetting with AI. This lead updates when it finishes (usually 2–4 minutes).")} disabled={busy} title={busy ? "An AI job is already running" : "AI reads their site, the sources and recent news, confirms the facts and finds decision-makers. Every contact it reports is checked against the page it came from."} style={btn(ACCENT, { opacity: busy ? 0.5 : 1 })}>Vet with AI</button>
          <button onClick={recheck} disabled={saving} title="Free: reads their contact pages again, re-opens the signal sources and checks their mail domain" style={ghost}>Re-check & vet</button>
          {lead.is_bid && <button onClick={() => startJob("bid", "Reading the full RFP. This lead updates when it finishes (usually 2–5 minutes).")} disabled={busy} style={{ ...ghost, opacity: busy ? 0.5 : 1 }}>{lead.bid ? "Re-read full RFP" : "Get full RFP"}</button>}
          <div style={{ position: "relative" }}>
            <button onClick={() => setAssignOpen(v => !v)} title="Give this lead to a staff member. They get a reminder email and a My Day task on the follow-up date."
              style={assignee ? { ...ghost, color: GOOD, borderColor: "rgba(52,211,153,0.45)" } : ghost}>
              {assignee ? `Assigned: ${assignee.name}` : "Assign"} ▾
            </button>
            {assignOpen && (
              <div style={{ ...panel, position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 5, background: "var(--t-page, #111b2f)", minWidth: 240, padding: 4, boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}>
                {staff.filter(s => s.is_active).map(s => (
                  <button key={s.id} onClick={() => assign(s.id)} disabled={s.id === lead.assigned_to}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, borderRadius: 6, padding: "7px 10px", fontSize: 13, cursor: s.id === lead.assigned_to ? "default" : "pointer", color: s.id === lead.assigned_to ? GOOD : "var(--t-text-body)", fontWeight: s.id === lead.assigned_to ? 700 : 500 }}>
                    {s.id === lead.assigned_to ? "✓ " : ""}{s.name}
                    {!s.has_access && <span style={{ ...small, fontSize: 11 }}> · email + My Day only</span>}
                  </button>
                ))}
                {lead.assigned_to && <button onClick={() => assign(null)} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, borderTop: "1px solid var(--t-border-soft)", padding: "7px 10px", fontSize: 13, cursor: "pointer", color: BAD }}>Unassign</button>}
              </div>
            )}
          </div>
          <button onClick={del} style={{ ...ghost, color: "#f87171", borderColor: "rgba(248,113,113,0.4)" }}>Delete</button>
        </div>
        {msg && <div style={{ fontSize: 12, color: "var(--t-text-body)", marginBottom: 12 }}>{msg}</div>}

        {section("Who to contact", <ContactList lead={lead} onUse={useContact} onRemove={removeContact} />,
          verifiedCount ? <span style={{ ...small, fontSize: 11 }}>{verifiedCount} confirmed</span> : null)}
        {section("Vetting", <Vetting v={lead.vetting} />)}
        {lead.is_bid && section("Full RFP", <BidBrief lead={lead} busy={busy} onStart={() => startJob("bid", "Reading the full RFP. This lead updates when it finishes (usually 2–5 minutes).")} />)}
        {section("Introduction email", <IntroEmail lead={lead} onSaved={onSaved} />)}
        {(lead.company_info && Object.keys(lead.company_info).length > 0) && section("Company facts", <CompanyInfo lead={lead} />)}

        {lead.summary && section("Why this lead", <div style={{ fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.5 }}>{lead.summary}</div>)}
        {needs.length > 0 && section("What they need", (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.5 }}>
            {needs.map((n, i) => <li key={i}><b>{n.need}</b>{n.evidence ? <span style={{ color: "var(--t-text-faint)" }}> — {n.evidence}</span> : null}{n.source_url && <> <a href={n.source_url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, fontSize: 12 }}>source</a></>}</li>)}
          </ul>
        ))}
        {points.length > 0 && section("Talking points for the first call", (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.5 }}>{points.map((p, i) => <li key={i}>{p}</li>)}</ul>
        ))}
        {lead.pitch && section("Suggested opening", (
          <div style={{ ...panel, padding: 12, fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.5 }}>
            {lead.pitch}
            <div><button onClick={() => { copy(lead.pitch); setMsg("Pitch copied."); }} style={{ ...ghost, padding: "3px 10px", fontSize: 12, marginTop: 8 }}>Copy</button></div>
          </div>
        ))}

        {section("Signals", (lead.signals || []).length ? (lead.signals || []).map((s, i) => (
          <div key={i} style={{ fontSize: 13, color: "var(--t-text-body)", padding: "7px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
            <Chip color={ACCENT}>{SIGNAL_LABEL[s.type] || s.type}</Chip>
            {s.date && <span style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{s.date}</span>}
            {s.due_date && <span style={{ fontSize: 11, color: "var(--t-text-faint)" }}> · due {s.due_date}</span>}
            <div style={{ marginTop: 3 }}>{s.detail}</div>
            {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT, wordBreak: "break-all" }}>Source</a>}
            {s.source_check && (
              <span style={{ fontSize: 11, marginLeft: 8, color: s.source_check.ok === false ? BAD : s.source_check.ok && s.source_check.mentions !== false ? GOOD : WARN }}>
                {s.source_check.ok === false ? "✕ " : s.source_check.ok && s.source_check.mentions !== false ? "✓ " : ""}{s.source_check.note}
              </span>
            )}
          </div>
        )) : <div style={{ fontSize: 12, color: "var(--t-text-faint)" }}>No buying signals yet. Try Vet with AI.</div>)}

        {section("Score breakdown", <Breakdown b={lead.score_breakdown} />)}
        {section("Website & email check", <SiteFacts sc={lead.site_check} lead={lead} />)}

        {section("Primary contact", (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <div><span style={label}>Name</span><input style={input} value={f.contact_name || ""} onChange={set("contact_name")} /></div>
            <div><span style={label}>Title</span><input style={input} value={f.contact_title || ""} onChange={set("contact_title")} /></div>
            <div><span style={label}>Email</span><input style={input} value={f.contact_email || ""} onChange={set("contact_email")} /></div>
            <div><span style={label}>Phone</span><input style={input} value={f.contact_phone || ""} onChange={set("contact_phone")} /></div>
            <div style={{ gridColumn: "1 / -1" }}><span style={label}>Website</span><input style={input} value={f.website || ""} onChange={set("website")} placeholder="https://" /></div>
            {lead.contact_url && <a href={lead.contact_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT }}>Contact page</a>}
          </div>
        ))}

        {section("Deal value", (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><span style={label}>Monthly $</span><input type="number" min="0" style={input} value={f.est_monthly_value ?? ""} onChange={set("est_monthly_value")} /></div>
              <div><span style={label}>One-time project $</span><input type="number" min="0" style={input} value={f.est_project_value ?? ""} onChange={set("est_project_value")} /></div>
            </div>
            <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 5 }}>
              {lead.value_override ? <>You set these numbers. <button onClick={() => save({ est_monthly_value: null, est_project_value: null })} style={{ ...ghost, padding: "1px 8px", fontSize: 11 }}>Use the estimate</button></>
                : "Estimated from staff count, services needed and the pricing in Settings."}
            </div>
            {(lead.services || []).length > 0 && <div style={{ marginTop: 8 }}>{lead.services.map(s => <Chip key={s}>{SERVICE_LABEL[s] || s}</Chip>)}</div>}
          </div>
        ))}

        {section("Pipeline", (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><span style={label}>Status</span>
              <select style={input} value={f.status || "new"} onChange={set("status")}>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><span style={label}>Next follow-up</span><input type="date" style={input} value={f.next_follow_up || ""} onChange={set("next_follow_up")} />
              <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 4 }}>
                {assignee ? `${assignee.name} gets a reminder email and a My Day task at 8 AM ET on this date.` : "Saving a date assigns the lead to you: you get the reminder email and My Day task at 8 AM ET that day."}
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}><span style={label}>Notes</span><textarea style={{ ...input, minHeight: 90, resize: "vertical" }} value={f.notes || ""} onChange={set("notes")} /></div>
          </div>
        ))}
        <button onClick={() => save()} disabled={saving} style={btn(ACCENT, { width: "100%", padding: "10px 14px" })}>{saving ? "Saving…" : "Save changes"}</button>
        <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 10 }}>Found {et(lead.created_at)} ET · {lead.source === "manual" ? "added by hand" : "found by the AI scout"}</div>
      </aside>
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────
function SettingsModal({ onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [model, setModel] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    call("/leadgen/settings").then(r => {
      const x = r.settings;
      setModel(r.model);
      setS({ ...x, regions: x.regions.join("\n"), industries: x.industries.join("\n"), services: x.services.join("\n"), partner_types: x.partner_types.join("\n"), email_facts: (x.email_facts || r.defaults?.email_facts || []).join("\n") });
    }).catch(e => setErr(e.message));
  }, []);
  const set = (k) => (e) => setS(p => ({ ...p, [k]: e.target.value }));
  const lines = (v) => String(v || "").split("\n").map(x => x.trim()).filter(Boolean);
  const save = async () => {
    setSaving(true); setErr("");
    try {
      await call("/leadgen/settings", { method: "PUT", body: JSON.stringify({ ...s, regions: lines(s.regions), industries: lines(s.industries), services: lines(s.services), partner_types: lines(s.partner_types), email_facts: lines(s.email_facts) }) });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };
  const area = (k, l, hint) => (
    <div style={{ marginBottom: 12 }}><span style={label}>{l}</span>
      <textarea style={{ ...input, minHeight: 80, resize: "vertical" }} value={s[k]} onChange={set(k)} />
      {hint && <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 3 }}>{hint}</div>}
    </div>
  );
  const num = (k, l) => <div><span style={label}>{l}</span><input type="number" style={input} value={s[k]} onChange={set(k)} /></div>;
  return (
    <Modal title="Lead search settings" onClose={onClose} width={640}>
      {!s ? <div style={{ fontSize: 13, color: "var(--t-text-faint)" }}>{err || "Loading…"}</div> : (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 12 }}>
            <div><span style={label}>Office location</span><input style={input} value={s.home_base} onChange={set("home_base")} placeholder="City, State" /></div>
            {num("local_radius_miles", "Local radius (miles)")}
          </div>
          <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: -6, marginBottom: 12 }}>Leads inside this radius get the Local Opportunity tag.</div>
          {area("regions", "Target regions", "One per line, e.g. North Carolina, South Carolina, or United States")}
          {area("industries", "Target industries", "\"Any industry\" searches everything. List industries one per line to prioritize them.")}
          {area("services", "Services MIDTC sells", "The AI uses this to judge fit.")}
          {area("partner_types", "Partner types to look for")}
          {area("email_facts", "What intro emails may say about MIDTC", "One fact per line. Drafts use only these facts about MIDTC, so keep them true.")}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
            {num("size_min", "Min staff")}
            {num("size_max", "Max staff")}
            {num("per_user_monthly", "Managed IT $/user/mo")}
            {num("web_care_monthly", "Website care $/mo")}
            {num("web_project_value", "Website build $")}
            {num("partner_monthly_value", "Partner value $/mo")}
            {num("leads_per_run", "Leads per search")}
            {num("searches_per_run", "Web searches per run")}
            {num("monthly_budget_usd", "Monthly AI budget $")}
          </div>
          <div style={{ marginBottom: 14 }}><span style={label}>Automatic search</span>
            <select style={input} value={s.auto_scout} onChange={set("auto_scout")}>
              <option value="off">Off — only when I click Find leads</option>
              <option value="weekly">Weekly — Mondays at 8 AM Eastern</option>
            </select>
          </div>
          <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginBottom: 12 }}>
            Searches stop for the month once the AI budget is reached. Saving re-scores every lead with the new pricing and size range. Model: {model}.
          </div>
          {err && <div style={{ fontSize: 12, color: "#f87171", marginBottom: 10 }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose} style={ghost}>Cancel</button>
            <button onClick={save} disabled={saving} style={btn(ACCENT)}>{saving ? "Saving…" : "Save settings"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function AddLeadModal({ onClose, onAdded, busy }) {
  const [f, setF] = useState({ lead_type: "client", company_name: "", website: "", location: "", contact_name: "", contact_email: "", contact_phone: "", notes: "", research: !busy });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async () => {
    setSaving(true); setErr("");
    try { const r = await call("/leadgen/leads", { method: "POST", body: JSON.stringify(f) }); onAdded(r); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  const field = (k, l, ph) => <div><span style={label}>{l}</span><input style={input} value={f[k]} onChange={set(k)} placeholder={ph} /></div>;
  return (
    <Modal title="Add a lead" onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 12 }}>
        {field("company_name", "Company *")}
        {field("website", "Website", "example.com")}
        <div><span style={label}>Type</span>
          <select style={input} value={f.lead_type} onChange={set("lead_type")}><option value="client">Client</option><option value="partner">Partner</option></select>
        </div>
        {field("location", "Location", "City, State")}
        {field("contact_name", "Contact name")}
        {field("contact_email", "Contact email")}
        {field("contact_phone", "Contact phone")}
      </div>
      <span style={label}>Notes</span>
      <textarea style={{ ...input, minHeight: 70, marginBottom: 10 }} value={f.notes} onChange={set("notes")} placeholder="How you heard about them, what they need…" />
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--t-text-body)", marginBottom: 14, opacity: busy ? 0.5 : 1 }}>
        <input type="checkbox" checked={f.research} disabled={busy} onChange={set("research")} />
        Vet this company with AI after adding (finds signals and decision-makers)
      </label>
      {err && <div style={{ fontSize: 12, color: "#f87171", marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} style={ghost}>Cancel</button>
        <button onClick={save} disabled={saving || !f.company_name.trim()} style={btn(ACCENT, { opacity: f.company_name.trim() ? 1 : 0.5 })}>{saving ? "Adding…" : "Add lead"}</button>
      </div>
    </Modal>
  );
}

const RUN_WHAT = { research: "Vet one lead with AI", bid: "Get full RFP", email: "Draft intro email" };

// ── Page ─────────────────────────────────────────────────────
export default function LeadGeneration() {
  const isMobile = useIsMobile();
  const [allowed, setAllowed] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [runs, setRuns] = useState([]);
  const [view, setView] = useState("leads");
  const [filters, setFilters] = useState({ status: "open", type: "", grade: "", local: "", web: "", q: "", sort: "score" });
  const [selected, setSelected] = useState(null);
  const [showScout, setShowScout] = useState(false);
  const [scout, setScout] = useState({ lead_type: "both", scope: "all", focus: "" });
  const [showSettings, setShowSettings] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [notice, setNotice] = useState("");
  const [err, setErr] = useState("");
  const [staff, setStaff] = useState([]);
  const [params, setParams] = useSearchParams();
  const pollRef = useRef(null);

  const loadLeads = useCallback(async () => {
    const qs = new URLSearchParams(Object.entries({ ...filters, ...(view === "bids" ? { view: "bids", sort: "" } : {}) }).filter(([, v]) => v)).toString();
    setLeads(await call(`/leadgen/leads?${qs}`));
  }, [filters, view]);
  const loadSummary = useCallback(async () => {
    const s = await call("/leadgen/summary");
    setSummary(s);
    if (s.last_run?.status === "running") setActiveRun(r => r || s.last_run);
  }, []);
  const loadRuns = useCallback(async () => setRuns(await call("/leadgen/runs")), []);

  useEffect(() => {
    call("/leadgen/access").then(() => setAllowed(true)).catch(e => setAllowed(e.status === 404 || e.status === 403 ? false : true));
  }, []);
  useEffect(() => { if (allowed) { loadSummary().catch(e => setErr(e.message)); loadRuns().catch(() => {}); call("/leadgen/assignees").then(setStaff).catch(() => {}); } }, [allowed, loadSummary, loadRuns]);
  // ?lead=<id> (reminder email, My Day) opens that lead.
  const deepLead = params.get("lead");
  useEffect(() => {
    if (!allowed || !deepLead) return;
    call(`/leadgen/leads/${encodeURIComponent(deepLead)}`).then(setSelected).catch(e => setErr(e.status === 404 ? "That lead no longer exists." : e.message));
    setParams({}, { replace: true });
  }, [allowed, deepLead, setParams]);
  useEffect(() => {
    if (!allowed) return;
    const t = setTimeout(() => loadLeads().catch(e => setErr(e.message)), filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [allowed, loadLeads, filters.q]);

  // Poll the running job until it finishes, then refresh everything.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!activeRun) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await call(`/leadgen/runs/${activeRun.id}`);
        if (r.status === "running") return;
        clearInterval(pollRef.current);
        setActiveRun(null);
        const cost = `$${(r.cost_cents / 100).toFixed(2)}`;
        setNotice(r.status === "done"
          ? (r.kind === "research" ? `Vetting finished. Cost ${cost}.`
            : r.kind === "bid" ? `Full RFP read. Cost ${cost}.`
              : `Search finished: ${r.leads_found} found, ${r.leads_new} new${r.leads_updated ? `, ${r.leads_updated} updated` : ""}. Every lead was vetted. Cost ${cost}.`)
          : `${r.kind === "scout" ? "Search" : RUN_WHAT[r.kind] || "Job"} failed: ${r.error || "unknown error"}`);
        await Promise.all([loadSummary(), loadLeads(), loadRuns()]);
        if (r.lead_id) {
          const fresh = (await call(`/leadgen/leads?q=`)).find(l => l.id === r.lead_id);
          setSelected(sel => (sel && sel.id === r.lead_id && fresh ? fresh : sel));
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeRun, loadSummary, loadLeads, loadRuns]);

  const startScout = async () => {
    setErr(""); setNotice("");
    try {
      const r = await call("/leadgen/runs", { method: "POST", body: JSON.stringify(scout) });
      setActiveRun(r.run); setShowScout(false);
      loadRuns().catch(() => {});
    } catch (e) { setErr(e.message); }
  };

  const exportCsv = async () => {
    try {
      const r = await fetch(API + "/leadgen/export.csv", { headers: { Authorization: `Bearer ${tok()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `midtc-leads-${todayEt()}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setErr(e.message); }
  };

  const quickStatus = async (lead, status) => {
    try {
      const u = await call(`/leadgen/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      setLeads(ls => ls.map(l => (l.id === u.id ? u : l)));
      loadSummary().catch(() => {});
    } catch (e) { setErr(e.message); }
  };

  if (allowed === null) return <div style={{ padding: 24, color: "var(--t-text-faint)" }}>Loading…</div>;
  if (!allowed) return <div style={{ padding: 24, color: "var(--t-text-faint)" }}>This page isn't available.</div>;

  const busy = !!activeRun || !!summary?.busy;
  const spent = (summary?.month_spend_cents || 0) / 100;
  const sel = (k) => (e) => setFilters(p => ({ ...p, [k]: e.target.value }));
  const th = (h) => <th key={h} style={{ padding: "10px 12px", borderBottom: "1px solid var(--t-border-soft)", fontWeight: 700 }}>{h}</th>;
  const statusSelect = (l) => (
    <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
      <select style={{ ...input, width: "auto", padding: "4px 8px", fontSize: 12 }} value={l.status} onChange={e => quickStatus(l, e.target.value)}>
        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </td>
  );
  const contactCell = (l) => (
    <td style={{ padding: "10px 12px", maxWidth: 230 }}>
      {l.contact_name || l.contact_email || l.contact_phone ? (
        <>
          <div style={{ fontWeight: 700, color: "var(--t-text-primary)", fontSize: 12 }}>{l.contact_name || inboxLabel(l.contact_email) || "Main line"}{l.contact_title ? <span style={{ fontWeight: 400, color: "var(--t-text-faint)" }}> · {l.contact_title}</span> : null}</div>
          <div style={{ fontSize: 12, color: "var(--t-text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.contact_email || l.contact_phone}</div>
          {(l.contacts || []).length > 1 && <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>+{l.contacts.length - 1} more</div>}
        </>
      ) : <span style={{ color: "var(--t-text-faint)", fontSize: 12 }}>No contact yet</span>}
    </td>
  );

  return (
    // width:100% matters: <main> is a flex column and margin:auto would otherwise
    // shrink-wrap this div to the table's min-width, pushing the page off-screen on phones.
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1400, width: "100%", minWidth: 0, boxSizing: "border-box", margin: "0 auto" }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)" }}>Lead Generation</h1>
          <div style={{ fontSize: 13, color: "var(--t-text-faint)", marginTop: 3 }}>Organizations that look ready to buy IT support or web development, or to partner with MIDTC. Only approved MIDTC staff can see this page.</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button onClick={() => setShowScout(v => !v)} disabled={busy} style={btn(ACCENT, { opacity: busy ? 0.5 : 1 })}>Find leads</button>
          <button onClick={() => setShowAdd(true)} style={ghost}>Add lead</button>
          <button onClick={exportCsv} style={ghost}>Export CSV</button>
          <button onClick={() => setShowSettings(true)} style={ghost}>Settings</button>
        </div>
      </div>

      {showScout && !busy && (
        <div style={{ ...panel, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "190px 230px 1fr auto", gap: 10, alignItems: "end" }}>
            <div><span style={label}>Looking for</span>
              <select style={input} value={scout.lead_type} onChange={e => setScout(p => ({ ...p, lead_type: e.target.value }))}>
                <option value="both">Clients and partners</option>
                <option value="client">Clients only</option>
                <option value="partner">Partners only</option>
                <option value="web_presence">Local businesses with no or weak website</option>
              </select>
            </div>
            <div><span style={label}>Where</span>
              <select style={input} value={scout.lead_type === "web_presence" ? "local" : scout.scope} disabled={scout.lead_type === "web_presence"} onChange={e => setScout(p => ({ ...p, scope: e.target.value }))}>
                <option value="all">Local + all target regions</option>
                <option value="local">Local only ({summary?.local_radius_miles ?? 50} mi of {summary?.home_base || "office"})</option>
              </select>
            </div>
            <div><span style={label}>Focus (optional)</span>
              <input style={input} value={scout.focus} onChange={e => setScout(p => ({ ...p, focus: e.target.value }))} placeholder="e.g. dental offices, construction companies, or law firms hiring IT staff" />
            </div>
            <button onClick={startScout} style={btn(ACCENT, { padding: "9px 18px" })}>Start search</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 8 }}>
            The AI searches the web; then MIDTC reads each company's own website for contacts, re-opens the sources and checks their email setup. Takes a few minutes. Spent this month: ${spent.toFixed(2)} of ${summary?.monthly_budget_usd ?? "—"} budget.
          </div>
        </div>
      )}

      {busy && (
        <div style={{ ...panel, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, borderColor: ACCENT }}>
          <div style={{ width: 18, height: 18, border: `3px solid #4f6fff`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: "var(--t-text-body)" }}>
            {activeRun?.kind === "research" ? "Vetting a lead with AI…" : activeRun?.kind === "bid" ? "Reading the full RFP…" : "Searching the web for leads and vetting each one…"} This usually takes 2–5 minutes. You can leave this page; results are saved when it finishes.
          </div>
        </div>
      )}
      {notice && <div style={{ ...panel, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "var(--t-text-body)", display: "flex", justifyContent: "space-between", gap: 10 }}><span>{notice}</span><button onClick={() => setNotice("")} style={{ ...ghost, padding: "0 8px" }}>✕</button></div>}
      {err && <div style={{ ...panel, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#f87171", borderColor: "rgba(248,113,113,0.4)", display: "flex", justifyContent: "space-between", gap: 10 }}><span>{err}</span><button onClick={() => setErr("")} style={{ ...ghost, padding: "0 8px" }}>✕</button></div>}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)", gap: 12, marginBottom: 16 }}>
        <Stat label="Hot leads" value={summary?.hot ?? "—"} sub={`${summary?.warm ?? 0} warm · ${summary?.open ?? 0} open · ${summary?.local ?? 0} local · ${summary?.vetted ?? 0} vetted`} color={GRADE.A.c} />
        <Stat label="Pipeline potential" value={moneyShort(summary?.pipeline_annual)} sub="per year, all open leads" />
        <Stat label="Weighted pipeline" value={moneyShort(summary?.weighted_annual)} sub="adjusted for each lead's stage" />
        <Stat label="Won" value={moneyShort(summary?.won_annual)} sub={`${summary?.won ?? 0} deals, per year`} color={GRADE.A.c} />
        <Stat label="AI spend this month" value={`$${spent.toFixed(2)}`} sub={`of $${summary?.monthly_budget_usd ?? "—"} budget${summary?.follow_ups_due ? ` · ${summary.follow_ups_due} follow-ups due` : ""}`} />
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 12, border: "1px solid var(--t-border-strong)", borderRadius: 8, width: "fit-content", overflow: "hidden" }}>
        {[["leads", "All leads"], ["bids", `Bids${summary?.bids_open != null ? ` (${summary.bids_open} open)` : ""}`]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} style={{ background: view === k ? ACCENT : "transparent", color: view === k ? "#fff" : "var(--t-text-body)", border: 0, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{l}</button>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <input style={{ ...input, width: isMobile ? "100%" : 240 }} value={filters.q} onChange={sel("q")} placeholder="Search company, industry, location" />
        <select style={{ ...input, width: "auto" }} value={filters.status} onChange={sel("status")}>
          <option value="open">Open leads</option><option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {view === "leads" && <>
          <select style={{ ...input, width: "auto" }} value={filters.type} onChange={sel("type")}>
            <option value="">Clients & partners</option><option value="client">Clients</option><option value="partner">Partners</option>
          </select>
          <select style={{ ...input, width: "auto" }} value={filters.local} onChange={sel("local")}>
            <option value="">All locations</option><option value="1">Local opportunities</option>
          </select>
          <select style={{ ...input, width: "auto" }} value={filters.web} onChange={sel("web")}>
            <option value="">Any web presence</option><option value="weak">No or weak website</option>
          </select>
          <select style={{ ...input, width: "auto" }} value={filters.grade} onChange={sel("grade")}>
            <option value="">All grades</option>
            {Object.entries(GRADE).map(([k, g]) => <option key={k} value={k}>{k} · {g.word}</option>)}
          </select>
          <select style={{ ...input, width: "auto" }} value={filters.sort} onChange={sel("sort")}>
            <option value="score">Sort: score</option><option value="value">Sort: deal value</option>
            <option value="newest">Sort: newest</option><option value="follow_up">Sort: follow-up date</option>
          </select>
        </>}
      </div>

      <div style={{ ...panel, overflowX: "auto" }}>
        {leads.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", fontSize: 13, color: "var(--t-text-faint)" }}>
            {view === "bids" ? "No bids match. RFP leads appear here when the scout finds open solicitations." : <>No leads match. Click <b>Find leads</b> to run a search, or <b>Add lead</b> to enter one you know about.</>}
          </div>
        ) : view === "bids" ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 980 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {["Due", "Bid", "Organization", "Bid contact", "Full RFP", "Est. / yr", "Status"].map(th)}
              </tr>
            </thead>
            <tbody>
              {leads.map(l => {
                const rfp = (l.signals || []).find(s => s.type === "rfp");
                return (
                  <tr key={l.id} onClick={() => setSelected(l)} style={{ cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)", opacity: l.bid_days_left != null && l.bid_days_left < 0 ? 0.6 : 1 }}>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 800, color: dueColor(l.bid_days_left) }}>{dueWords(l.bid_days_left)}</div>
                      <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{l.bid_due ? day(l.bid_due) : ""}</div>
                    </td>
                    <td style={{ padding: "10px 12px", maxWidth: 340, color: "var(--t-text-body)" }}>
                      <div style={{ fontWeight: 700, color: "var(--t-text-primary)" }}>{l.bid?.title || (rfp ? rfp.detail.split(/[.;]/)[0] : "Open solicitation")}</div>
                      {l.bid?.solicitation_number && <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>#{l.bid.solicitation_number}</div>}
                    </td>
                    <td style={{ padding: "10px 12px", maxWidth: 240 }}>
                      <div style={{ fontWeight: 700, color: "var(--t-text-primary)" }}>{l.company_name}</div>
                      <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{[l.location, l.is_local ? "Local" : null].filter(Boolean).join(" · ")}</div>
                      <VetChip v={l.vetting} />
                    </td>
                    {contactCell(l)}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", fontSize: 12, color: l.bid ? GOOD : "var(--t-text-faint)" }}>{l.bid ? `✓ ${(l.bid.documents || []).filter(d => !d.error).length} doc${(l.bid.documents || []).filter(d => !d.error).length === 1 ? "" : "s"}` : "Not read yet"}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "var(--t-text-primary)", whiteSpace: "nowrap" }}>{money(l.est_annual_value)}</td>
                    {statusSelect(l)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1040 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {["Score", "Company", "Contact", "Needs", "Top signal", "Est. / yr", "Status", "Assigned", "Follow-up"].map(th)}
              </tr>
            </thead>
            <tbody>
              {leads.map(l => {
                const top = (l.signals || [])[0];
                const due = l.next_follow_up && l.next_follow_up <= todayEt() && !["won", "lost", "not_a_fit"].includes(l.status);
                return (
                  <tr key={l.id} onClick={() => setSelected(l)} style={{ cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)" }}>
                    <td style={{ padding: "10px 12px" }}><GradeBadge grade={l.grade} score={l.score} /></td>
                    <td style={{ padding: "10px 12px", maxWidth: 260 }}>
                      {l.is_local && <div><LocalTag miles={l.distance_miles} /></div>}
                      <div style={{ fontWeight: 700, color: "var(--t-text-primary)" }}>{l.company_name}</div>
                      <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{[l.lead_type === "partner" ? "Partner" : "Client", l.domain, l.location].filter(Boolean).join(" · ")}</div>
                      {WEAK.includes(l.web_presence) && <div style={{ fontSize: 11, color: "#fb923c", fontWeight: 700 }}>{PRESENCE_LABEL[l.web_presence]}</div>}
                      <VetChip v={l.vetting} />{l.is_bid && <Chip color={dueColor(l.bid_days_left)}>Bid · {dueWords(l.bid_days_left)}</Chip>}
                    </td>
                    {contactCell(l)}
                    <td style={{ padding: "10px 12px", maxWidth: 200 }}>{(l.services || []).slice(0, 3).map(s => <Chip key={s}>{SERVICE_LABEL[s] || s}</Chip>)}</td>
                    <td style={{ padding: "10px 12px", maxWidth: 300, color: "var(--t-text-body)" }}>
                      {top ? <><b style={{ fontSize: 12 }}>{SIGNAL_LABEL[top.type] || top.type}</b><div style={{ fontSize: 12, color: "var(--t-text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{top.detail}</div></> : <span style={{ color: "var(--t-text-faint)" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "var(--t-text-primary)", whiteSpace: "nowrap" }}>{money(l.est_annual_value)}</td>
                    {statusSelect(l)}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", fontSize: 12, color: l.assigned_to ? "var(--t-text-body)" : "var(--t-text-faint)" }}>{l.assigned_to ? shortName(staff.find(s => s.id === l.assigned_to)?.name) || "—" : "—"}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: due ? "#fb923c" : "var(--t-text-faint)", fontWeight: due ? 700 : 400 }}>{l.next_follow_up || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowRuns(v => !v)} style={{ ...ghost, fontSize: 12 }}>{showRuns ? "Hide" : "Show"} search history</button>
        {showRuns && (
          <div style={{ ...panel, overflowX: "auto", marginTop: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--t-text-faint)", textTransform: "uppercase", fontSize: 11 }}>
                  {["Started (ET)", "What", "Result", "Searches", "Cost", ""].map(h => <th key={h} style={{ padding: "8px 12px", borderBottom: "1px solid var(--t-border-soft)" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--t-border-soft)", color: "var(--t-text-body)" }}>
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{et(r.started_at)}</td>
                    <td style={{ padding: "8px 12px" }}>{RUN_WHAT[r.kind] || (r.lead_type === "web_presence" ? "Find local businesses with weak websites" : `Find ${r.lead_type === "both" ? "clients & partners" : r.lead_type + "s"}`)}{r.scope === "local" ? " (local only)" : ""}{r.focus ? ` — ${r.focus}` : ""}{r.trigger === "schedule" ? " (weekly)" : ""}</td>
                    <td style={{ padding: "8px 12px" }}>{r.status === "running" ? "Running…" : r.status === "error" ? <span style={{ color: "#f87171" }}>{r.error || "Failed"}</span> : RUN_WHAT[r.kind] ? "Done" : `${r.leads_found} found, ${r.leads_new} new`}</td>
                    <td style={{ padding: "8px 12px" }}>{r.searches}</td>
                    <td style={{ padding: "8px 12px" }}>${(r.cost_cents / 100).toFixed(2)}</td>
                    <td />
                  </tr>
                ))}
                {!runs.length && <tr><td colSpan={6} style={{ padding: 14, color: "var(--t-text-faint)" }}>No searches yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <LeadDrawer
          lead={selected} staff={staff} isMobile={isMobile} busy={busy}
          onClose={() => setSelected(null)}
          onSaved={(u) => { setSelected(u); setLeads(ls => ls.map(l => (l.id === u.id ? u : l))); loadSummary().catch(() => {}); }}
          onDeleted={(id) => { setSelected(null); setLeads(ls => ls.filter(l => l.id !== id)); loadSummary().catch(() => {}); }}
          onRunStarted={(run) => setActiveRun(run)}
        />
      )}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); setNotice("Settings saved. All leads were re-scored."); loadLeads().catch(() => {}); loadSummary().catch(() => {}); }} />}
      {showAdd && <AddLeadModal busy={busy} onClose={() => setShowAdd(false)} onAdded={(r) => { setShowAdd(false); if (r.run) setActiveRun(r.run); setNotice(r.research_error ? `Lead added. AI vetting didn't start: ${r.research_error}` : r.created ? "Lead added. Its website is being read for contacts now." : "That company was already in your leads, so it was updated."); loadLeads().catch(() => {}); loadSummary().catch(() => {}); }} />}
    </div>
  );
}
